/**
 * Quest Board Manager
 *
 * Handles procedural quest generation for market quest boards.
 * Generates quests from templates with randomized parameters.
 * Caches generated quests and refreshes on a configurable interval.
 */

import { QuestDefinition, QuestObjective, QuestReward, QuestRewardType, Quality } from '@/types';
import { getProceduralTemplate } from '@/data';

/** Default number of procedural quests per zone */
const DEFAULT_MAX_PROCEDURAL_QUESTS = 5;

/** Default refresh interval in milliseconds (5 minutes) */
const DEFAULT_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/** localStorage key for persisted quest board cache */
const CACHE_STORAGE_KEY = 'quest_board_cache';

/** Cached quest board state for a single market */
interface CachedQuestBoard {
  quests: QuestDefinition[];
  /** Timestamps when pending refill slots will be ready */
  refillAt: number[];
  /** Template pool used for this board (for checking if new quests can generate) */
  templateIds?: string[];
}

/** Serializable format for localStorage */
interface SerializedCache {
  cache: Record<string, CachedQuestBoard>;
  buddyCache: Record<string, CachedQuestBoard>;
}

/** Default number of buddy quests per zone */
const DEFAULT_MAX_BUDDY_QUESTS = 5;

export class QuestBoardManager {
  /** Cached procedural quests per market ID */
  private cache: Map<string, CachedQuestBoard> = new Map();

  /** Cached buddy quests per market ID */
  private buddyCache: Map<string, CachedQuestBoard> = new Map();

  /** Max procedural quests per zone/market */
  private maxProceduralQuests: number = DEFAULT_MAX_PROCEDURAL_QUESTS;

  /** Max buddy quests per zone/market */
  private maxBuddyQuests: number = DEFAULT_MAX_BUDDY_QUESTS;

  /** Refresh interval in ms */
  private refreshIntervalMs: number = DEFAULT_REFRESH_INTERVAL_MS;

  /**
   * Configure limits (useful for testing)
   */
  configure(options: { maxProceduralQuests?: number; refreshIntervalMs?: number; maxBuddyQuests?: number }): void {
    if (options.maxProceduralQuests !== undefined) {
      this.maxProceduralQuests = options.maxProceduralQuests;
    }
    if (options.refreshIntervalMs !== undefined) {
      this.refreshIntervalMs = options.refreshIntervalMs;
    }
    if (options.maxBuddyQuests !== undefined) {
      this.maxBuddyQuests = options.maxBuddyQuests;
    }
  }

  /**
   * Get the current max procedural quests setting
   */
  getMaxProceduralQuests(): number {
    return this.maxProceduralQuests;
  }

  /**
   * Get the current max buddy quests setting
   */
  getMaxBuddyQuests(): number {
    return this.maxBuddyQuests;
  }

  /**
   * Get the current refresh interval in ms
   */
  getRefreshIntervalMs(): number {
    return this.refreshIntervalMs;
  }

  /**
   * Generate a random number within a range
   */
  private randomInRange(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * Pick a random element from an array
   */
  private randomChoice<T>(array: T[]): T {
    return array[Math.floor(Math.random() * array.length)];
  }

  /**
   * Generate a random quality level for quest filters
   */
  private randomQuality(): Quality {
    const qualities: Quality[] = [
      Quality.COMMON,
      Quality.UNCOMMON,
      Quality.RARE,
      Quality.EPIC,
      Quality.LEGENDARY,
    ];

    // Weight towards lower qualities (matching QualityChances distribution)
    // Common: 50%, Uncommon: 30%, Rare: 15%, Epic: 4%, Legendary: 1%
    const weights = [0.50, 0.30, 0.15, 0.04, 0.01];
    const random = Math.random();
    let cumulative = 0;

    for (let i = 0; i < qualities.length; i++) {
      cumulative += weights[i];
      if (random <= cumulative) {
        return qualities[i];
      }
    }

    return Quality.COMMON; // Default to common if somehow no match
  }

  /**
   * Format a species ID into a readable name
   */
  private formatSpeciesName(speciesId: string): string {
    return speciesId
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Format a quality enum into a readable name
   */
  private formatQualityName(quality: Quality): string {
    return quality.charAt(0).toUpperCase() + quality.slice(1);
  }

  /**
   * Generate a unique quest ID
   */
  private generateQuestId(templateId: string): string {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `${templateId}_${timestamp}_${random}`;
  }

  /**
   * Extract the template ID from a generated quest ID.
   * Quest IDs are formatted as: {templateId}_{timestamp}_{random}
   * Template IDs contain underscores too, so we strip the last two segments.
   */
  private extractTemplateId(questId: string): string {
    const parts = questId.split('_');
    if (parts.length > 2) {
      return parts.slice(0, -2).join('_');
    }
    return questId;
  }

  /**
   * Generate a procedural quest from a template
   */
  generateQuest(templateId: string, availableSpecies?: string[]): QuestDefinition | null {
    const template = getProceduralTemplate(templateId);
    if (!template) {
      console.warn(`Template ${templateId} not found`);
      return null;
    }

    // Generate random target value
    const targetValue = this.randomInRange(template.targetRange[0], template.targetRange[1]);

    // Generate quest ID
    const questId = this.generateQuestId(templateId);

    // Initialize variables for template substitution
    let name = template.nameTemplate;
    let description = template.descriptionTemplate;
    let speciesFilter: string | undefined;
    let qualityFilter: Quality | undefined;

    // Apply filters and substitutions
    if (template.useSpeciesFilter) {
      const species = availableSpecies && availableSpecies.length > 0
        ? availableSpecies
        : ['rainbow_trout', 'brown_trout', 'brook_trout']; // Fallback
      const randomSpecies = this.randomChoice(species);
      speciesFilter = randomSpecies;

      const speciesName = this.formatSpeciesName(randomSpecies);
      name = name.replace('{species}', speciesName);
      description = description.replace('{species}', speciesName);
    }

    if (template.useQualityFilter) {
      const randomQuality = this.randomQuality();
      qualityFilter = randomQuality;

      const qualityName = this.formatQualityName(randomQuality);
      name = name.replace('{quality}', qualityName);
      description = description.replace('{quality}', qualityName);
    }

    // Replace count placeholder
    name = name.replace('{count}', targetValue.toString());
    description = description.replace('{count}', targetValue.toString());

    // Create objective
    const objective: QuestObjective = {
      id: 'objective_1',
      type: template.objectiveType,
      description: description,
      targetValue,
    };

    // Add filters if applicable
    if (speciesFilter || qualityFilter) {
      objective.filters = {};
      if (speciesFilter) {
        objective.filters.speciesId = speciesFilter;
      }
      if (qualityFilter) {
        objective.filters.quality = qualityFilter;
      }
    }

    // Calculate reward
    const rewardAmount = Math.floor(
      template.baseReward + template.rewardPerTarget * targetValue
    );

    const reward: QuestReward = {
      type: QuestRewardType.CURRENCY,
      value: rewardAmount,
      description: `+${rewardAmount} gold`,
    };

    // Build quest definition
    const quest: QuestDefinition = {
      id: questId,
      name,
      description,
      type: template.type,
      objectives: [objective],
      rewards: [reward],
      repeatable: false,
      isTutorial: false,
      category: template.category,

      // Procedural quests expire after 24 hours
      expiresAt: Date.now() + 24 * 60 * 60 * 1000,
    };

    return quest;
  }

  /**
   * Generate multiple quests from a pool of templates.
   * Each template is used at most once per generation call.
   * Pass usedTemplateIds to also exclude templates already in the cache.
   */
  generateQuestsFromPool(
    templateIds: string[],
    count: number,
    availableSpecies?: string[],
    usedTemplateIds?: Set<string>
  ): QuestDefinition[] {
    const quests: QuestDefinition[] = [];
    const used = new Set(usedTemplateIds ?? []);

    for (let i = 0; i < count; i++) {
      // Filter to templates not already used
      const available = templateIds.filter((id) => !used.has(id));
      if (available.length === 0) break;

      const templateId = this.randomChoice(available);
      const quest = this.generateQuest(templateId, availableSpecies);

      if (quest) {
        quests.push(quest);
        used.add(templateId);
      }
    }

    return quests;
  }

  /**
   * Get cached procedural quests for a market, generating if needed.
   *
   * Refill-based system:
   * - First visit: generates 1 quest, schedules (max-1) ramp-up refills
   * - Each refill adds 1 quest after refreshIntervalMs
   * - When a quest is removed (accepted), a new refill is scheduled
   * - Never exceeds maxProceduralQuests
   */
  getCachedQuests(
    marketId: string,
    templateIds: string[],
    availableSpecies?: string[]
  ): QuestDefinition[] {
    const now = Date.now();
    const cached = this.cache.get(marketId);

    if (!cached) {
      // First visit: generate 1 quest, schedule ramp-up refills
      const quests = this.generateQuestsFromPool(templateIds, 1, availableSpecies);
      const refillAt: number[] = [];
      for (let i = 1; i < this.maxProceduralQuests; i++) {
        refillAt.push(now + i * this.refreshIntervalMs);
      }
      this.cache.set(marketId, { quests, refillAt, templateIds });
      this.saveCache();
      return quests;
    }

    // Keep template pool up to date
    cached.templateIds = templateIds;

    // Process ready refills
    const stillPending: number[] = [];
    let refillsReady = 0;
    for (const time of cached.refillAt) {
      if (now >= time) {
        refillsReady++;
      } else {
        stillPending.push(time);
      }
    }
    cached.refillAt = stillPending;

    // Generate new quests for ready refills (up to max)
    // Exclude templates already represented in the cache to avoid duplicates
    const slotsAvailable = this.maxProceduralQuests - cached.quests.length;
    const toGenerate = Math.min(refillsReady, slotsAvailable);
    if (toGenerate > 0) {
      const usedTemplateIds = new Set(
        cached.quests.map((q) => this.extractTemplateId(q.id))
      );
      const newQuests = this.generateQuestsFromPool(templateIds, toGenerate, availableSpecies, usedTemplateIds);
      cached.quests.push(...newQuests);
      this.saveCache();
    }

    return cached.quests;
  }

  /**
   * Check if the cache for a market has refills ready to process
   */
  isCacheExpired(marketId: string): boolean {
    const cached = this.cache.get(marketId);
    if (!cached) return true;

    if (cached.quests.length >= this.maxProceduralQuests) return false;

    const now = Date.now();
    return cached.refillAt.some((t) => now >= t);
  }

  /**
   * Get the time remaining until the next quest is added (in ms).
   * Returns 0 if at max capacity with no pending refills, or no cache exists.
   */
  getTimeUntilRefresh(marketId: string): number {
    const cached = this.cache.get(marketId);
    if (!cached) return 0;

    if (cached.refillAt.length === 0) return 0;

    // Find the earliest pending refill
    const nextRefill = Math.min(...cached.refillAt);
    return Math.max(0, nextRefill - Date.now());
  }

  /**
   * Check if ANY quest board (procedural or buddy) has refills ready
   * AND new quests would actually be generated.
   * Used by the QuestDock to show "new quests available" notifications.
   *
   * Guards against false positives:
   * - Cache already at max capacity (no room for new quests)
   * - All unique templates already represented in cache (dedup would block generation)
   * - No ready refill timestamps
   */
  hasAnyRefillsReady(): boolean {
    const now = Date.now();

    for (const cached of this.cache.values()) {
      if (!this.canGenerateNewQuest(cached, this.maxProceduralQuests, now)) continue;
      return true;
    }
    for (const cached of this.buddyCache.values()) {
      if (!this.canGenerateNewQuest(cached, this.maxBuddyQuests, now)) continue;
      return true;
    }
    return false;
  }

  /**
   * Check if any quest board cache has quests that the player hasn't accepted or completed.
   * Used on game load to notify returning players about available quests on the board.
   */
  hasUnacceptedCachedQuests(
    activeQuestIds: Set<string>,
    completedQuestIds: Set<string>
  ): boolean {
    for (const cached of this.cache.values()) {
      for (const quest of cached.quests) {
        if (!activeQuestIds.has(quest.id) && !completedQuestIds.has(quest.id)) {
          return true;
        }
      }
    }
    for (const cached of this.buddyCache.values()) {
      for (const quest of cached.quests) {
        if (!activeQuestIds.has(quest.id) && !completedQuestIds.has(quest.id)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Check whether a cached board has ready refills that would actually produce quests.
   */
  private canGenerateNewQuest(cached: CachedQuestBoard, maxQuests: number, now: number): boolean {
    // No ready refills
    if (!cached.refillAt.some((t) => now >= t)) return false;

    // Already at max capacity
    if (cached.quests.length >= maxQuests) return false;

    // All unique templates already in cache (dedup would block generation)
    if (cached.templateIds && cached.templateIds.length > 0) {
      const usedTemplates = new Set(
        cached.quests.map((q) => this.extractTemplateId(q.id))
      );
      const hasUnusedTemplate = cached.templateIds.some((id) => !usedTemplates.has(id));
      if (!hasUnusedTemplate) return false;
    }

    return true;
  }

  /**
   * Get the earliest upcoming refill timestamp across all caches
   * that could actually produce a new quest.
   * Returns null if no actionable refills are pending.
   * Used by the QuestDock to schedule precise notification timing.
   */
  getNextRefillTime(): number | null {
    let earliest: number | null = null;

    const checkCache = (cached: CachedQuestBoard, maxQuests: number) => {
      // Skip caches that can't generate new quests
      if (cached.quests.length >= maxQuests) return;
      if (cached.templateIds && cached.templateIds.length > 0) {
        const usedTemplates = new Set(
          cached.quests.map((q) => this.extractTemplateId(q.id))
        );
        if (!cached.templateIds.some((id) => !usedTemplates.has(id))) return;
      }

      for (const t of cached.refillAt) {
        if (earliest === null || t < earliest) earliest = t;
      }
    };

    for (const cached of this.cache.values()) {
      checkCache(cached, this.maxProceduralQuests);
    }
    for (const cached of this.buddyCache.values()) {
      checkCache(cached, this.maxBuddyQuests);
    }
    return earliest;
  }

  /**
   * Force refresh the cache for a market
   */
  invalidateCache(marketId: string): void {
    this.cache.delete(marketId);
    this.saveCache();
  }

  /**
   * Clear all cached quest boards (procedural + buddy)
   */
  clearAllCaches(): void {
    this.cache.clear();
    this.buddyCache.clear();
    this.saveCache();
  }

  /**
   * Persist the quest board cache to localStorage.
   * Called after every cache mutation so quests survive page reload.
   */
  saveCache(): void {
    try {
      const data: SerializedCache = {
        cache: Object.fromEntries(this.cache),
        buddyCache: Object.fromEntries(this.buddyCache),
      };
      localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('[QuestBoardManager] Failed to save cache:', error);
    }
  }

  /**
   * Restore the quest board cache from localStorage.
   * Prunes expired quests (expiresAt in the past) on load.
   */
  loadCache(): void {
    try {
      const stored = localStorage.getItem(CACHE_STORAGE_KEY);
      if (!stored) return;

      const data: SerializedCache = JSON.parse(stored);
      const now = Date.now();

      // Restore procedural cache, pruning expired quests
      if (data.cache) {
        for (const [marketId, board] of Object.entries(data.cache)) {
          const validQuests = board.quests.filter(
            (q) => !q.expiresAt || q.expiresAt > now
          );
          const expiredCount = board.quests.length - validQuests.length;

          // Schedule refills for expired quests that were pruned
          const refillAt = [...board.refillAt];
          for (let i = 0; i < expiredCount; i++) {
            refillAt.push(now + this.refreshIntervalMs);
          }

          this.cache.set(marketId, { quests: validQuests, refillAt, templateIds: board.templateIds });
        }
      }

      // Restore buddy cache (same logic)
      if (data.buddyCache) {
        for (const [marketId, board] of Object.entries(data.buddyCache)) {
          const validQuests = board.quests.filter(
            (q) => !q.expiresAt || q.expiresAt > now
          );
          const expiredCount = board.quests.length - validQuests.length;

          const refillAt = [...board.refillAt];
          for (let i = 0; i < expiredCount; i++) {
            refillAt.push(now + this.refreshIntervalMs);
          }

          this.buddyCache.set(marketId, { quests: validQuests, refillAt, templateIds: board.templateIds });
        }
      }

      const totalQuests = Array.from(this.cache.values()).reduce((sum, b) => sum + b.quests.length, 0)
        + Array.from(this.buddyCache.values()).reduce((sum, b) => sum + b.quests.length, 0);
      if (totalQuests > 0) {
        console.log(`[QuestBoardManager] Restored cache: ${totalQuests} quests`);
      }
    } catch (error) {
      console.error('[QuestBoardManager] Failed to load cache:', error);
      localStorage.removeItem(CACHE_STORAGE_KEY);
    }
  }

  /**
   * Remove a specific quest from the cache (e.g., when accepted).
   * Schedules a refill so a new quest appears after refreshIntervalMs.
   */
  removeQuestFromCache(marketId: string, questId: string): void {
    const cached = this.cache.get(marketId);
    if (!cached) return;

    const hadQuest = cached.quests.some((q) => q.id === questId);
    cached.quests = cached.quests.filter((q) => q.id !== questId);

    // Schedule a replacement quest
    if (hadQuest) {
      cached.refillAt.push(Date.now() + this.refreshIntervalMs);
      this.saveCache();
    }
  }

  /**
   * Get cached buddy quests for a market, generating if needed.
   * Separate cache from regular procedural quests.
   *
   * Uses the same ramp-up refill system as procedural quests:
   * - First visit: generates 1 quest, schedules (max-1) ramp-up refills
   * - Each refill adds 1 quest after refreshIntervalMs
   * - When a quest is removed (accepted), a new refill is scheduled
   * - Never exceeds maxBuddyQuests
   */
  getCachedBuddyQuests(
    marketId: string,
    templateIds: string[],
    availableSpecies?: string[]
  ): QuestDefinition[] {
    const now = Date.now();
    const cached = this.buddyCache.get(marketId);

    if (!cached) {
      // First visit: generate 1 buddy quest, schedule ramp-up refills
      const quests = this.generateQuestsFromPool(templateIds, 1, availableSpecies);
      const refillAt: number[] = [];
      for (let i = 1; i < this.maxBuddyQuests; i++) {
        refillAt.push(now + i * this.refreshIntervalMs);
      }
      this.buddyCache.set(marketId, { quests, refillAt, templateIds });
      this.saveCache();
      return quests;
    }

    // Keep template pool up to date
    cached.templateIds = templateIds;

    // Process ready refills
    const stillPending: number[] = [];
    let refillsReady = 0;
    for (const time of cached.refillAt) {
      if (now >= time) {
        refillsReady++;
      } else {
        stillPending.push(time);
      }
    }
    cached.refillAt = stillPending;

    // Generate new quests for ready refills (up to max)
    // Exclude templates already represented in the cache to avoid duplicates
    const slotsAvailable = this.maxBuddyQuests - cached.quests.length;
    const toGenerate = Math.min(refillsReady, slotsAvailable);
    if (toGenerate > 0) {
      const usedTemplateIds = new Set(
        cached.quests.map((q) => this.extractTemplateId(q.id))
      );
      const newQuests = this.generateQuestsFromPool(templateIds, toGenerate, availableSpecies, usedTemplateIds);
      cached.quests.push(...newQuests);
      this.saveCache();
    }

    return cached.quests;
  }

  /**
   * Force refresh the buddy cache for a market
   */
  invalidateBuddyCache(marketId: string): void {
    this.buddyCache.delete(marketId);
    this.saveCache();
  }

  /**
   * Remove a specific quest from the buddy cache.
   * Schedules a refill so a new buddy quest appears after refreshIntervalMs.
   */
  removeQuestFromBuddyCache(marketId: string, questId: string): void {
    const cached = this.buddyCache.get(marketId);
    if (!cached) return;

    const hadQuest = cached.quests.some((q) => q.id === questId);
    cached.quests = cached.quests.filter((q) => q.id !== questId);

    // Schedule a replacement buddy quest
    if (hadQuest) {
      cached.refillAt.push(Date.now() + this.refreshIntervalMs);
      this.saveCache();
    }
  }
}

// Singleton instance
export const questBoardManager = new QuestBoardManager();
